import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactNode } from 'react';
import { DeviceStatusCard } from '../DeviceStatusCard';
import { useTheme } from '@mui/material';
import { CloudCheck, CloudOff, Router, Zap, ZapOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DraggableCard from '../../../components/DraggableCard';
import type { DragStackState } from '../../../hooks/useDragReorder';
import { StatsGrid } from './styles';

interface EquipmentStatsProps {
  userPreferences: UserPreferencesType;
  deviceStatus: DevicesStatusType;
  loading: boolean;
  editMode: boolean;
  dragStack: DragStackState;
}

function EquipmentStats({
  userPreferences,
  deviceStatus,
  loading,
  editMode,
  dragStack,
}: EquipmentStatsProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    order,
    draggingIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = dragStack;

  // The number of columns and rows is determined by the amount of visible cards
  const visibleCount = order.filter(
    (id) => userPreferences[id as keyof UserPreferencesType]
  ).length;
  const rows = visibleCount > 3 ? 2 : 1;
  const columns = Math.max(1, Math.ceil(visibleCount / rows));

  const statCards: Record<string, ReactNode> = {
    totalDevices: (
      <DeviceStatusCard
        title={t('dashboard.status.total')}
        total={loading ? 0 : deviceStatus?.total}
        value={loading ? 0 : deviceStatus?.total}
        color={theme.palette.primary.main}
        icon={<Router />}
      />
    ),
    totalOnlineDevices: (
      <DeviceStatusCard
        title={t('dashboard.status.online')}
        total={loading ? 0 : deviceStatus.total}
        value={loading ? 0 : deviceStatus.online}
        color={theme.palette.status.online}
        icon={<CloudCheck />}
      />
    ),
    totalOfflineDevices: (
      <DeviceStatusCard
        title={t('dashboard.status.offline')}
        total={loading ? 0 : deviceStatus.total}
        value={loading ? 0 : deviceStatus.offline}
        color={theme.palette.status.offline}
        icon={<CloudOff />}
      />
    ),
    totalEnabledDevices: (
      <DeviceStatusCard
        title={t('dashboard.status.enabled')}
        total={loading ? 0 : deviceStatus.total}
        value={loading ? 0 : deviceStatus.enabled}
        color={theme.palette.status.enabled}
        icon={<Zap />}
      />
    ),
    totalDisabledDevices: (
      <DeviceStatusCard
        title={t('dashboard.status.disabled')}
        total={loading ? 0 : deviceStatus.total}
        value={loading ? 0 : deviceStatus.disabled}
        color={theme.palette.status.disabled}
        icon={<ZapOff />}
      />
    ),
  };

  return (
    <StatsGrid isXs={isXs} columns={columns}>
      {order.map(
        (id, index) =>
          userPreferences[id as keyof UserPreferencesType] && (
            <DraggableCard
              key={id}
              editMode={editMode}
              sx={{ flex: 1, minWidth: '250px' }}
              isDragging={draggingIndex === index}
              isDragOver={dragOverIndex === index}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              {statCards[id]}
            </DraggableCard>
          )
      )}
    </StatsGrid>
  );
}

export default EquipmentStats;
